import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import {
  ArrowLeft, Mic, Square, Play, Pause, Trash2, Clock,
  BookOpen, StickyNote, ChevronDown, ChevronUp
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function formatDuration(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function RecordingItem({ recording, token, onDelete }) {
  const [playing, setPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);

  const loadAudio = useCallback(async () => {
    if (audioUrl) return audioUrl;
    setLoadingAudio(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/api/voice/recordings/${recording.id}/audio`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob([res.data], { type: res.headers['content-type'] || 'audio/webm' });
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      return url;
    } catch (e) {
      setError('Kunde inte ladda inspelningen');
      return null;
    } finally {
      setLoadingAudio(false);
    }
  }, [recording.id, token, audioUrl]);

  const togglePlay = async () => {
    const url = await loadAudio();
    if (!url) return;

    if (audioRef.current) {
      if (playing) {
        audioRef.current.pause();
        setPlaying(false);
      } else {
        try {
          await audioRef.current.play();
          setPlaying(true);
        } catch (e) {
          setError('Kunde inte spela upp — formatet stöds inte av din webbläsare');
        }
      }
    }
  };

  useEffect(() => {
    return () => {
      if (audioRef.current) audioRef.current.pause();
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  const isDiary = recording.category === 'diary';

  return (
    <div className="bg-card rounded-xl border border-border/30 overflow-hidden" data-testid={`recording-${recording.id}`}>
      <button onClick={() => setExpanded(!expanded)} className="w-full px-4 py-3 flex items-center gap-3 text-left">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isDiary ? 'bg-rose-100 text-rose-600 dark:bg-rose-950 dark:text-rose-400' : 'bg-sky-100 text-sky-600 dark:bg-sky-950 dark:text-sky-400'}`}>
          {isDiary ? <BookOpen className="w-5 h-5" /> : <StickyNote className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{recording.title}</p>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Clock className="w-3 h-3" />
            <span>{formatDuration(recording.duration)}</span>
            <span>·</span>
            <span>{formatDate(recording.created_at)}</span>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>

      {expanded && (
        <div className="px-4 pb-3 space-y-2">
          {/* Hidden audio element for native playback */}
          {audioUrl && (
            <audio
              ref={audioRef}
              src={audioUrl}
              onEnded={() => setPlaying(false)}
              onError={() => setError('Kunde inte spela upp inspelningen')}
              preload="auto"
            />
          )}
          
          <div className="flex items-center gap-2">
            <Button 
              size="sm" 
              variant={playing ? 'default' : 'outline'} 
              onClick={togglePlay} 
              disabled={loadingAudio}
              className="rounded-full" 
              data-testid={`play-${recording.id}`}
            >
              {loadingAudio ? (
                <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-1" />
              ) : playing ? (
                <Pause className="w-4 h-4 mr-1" />
              ) : (
                <Play className="w-4 h-4 mr-1" />
              )}
              {loadingAudio ? 'Laddar...' : playing ? 'Pausa' : 'Spela'}
            </Button>
            <Button size="sm" variant="destructive" onClick={() => onDelete(recording.id)} className="rounded-full" data-testid={`delete-${recording.id}`}>
              <Trash2 className="w-4 h-4 mr-1" />
              Ta bort
            </Button>
          </div>
          
          {error && (
            <p className="text-xs text-destructive px-1">{error}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function VoiceRecordingPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [recordings, setRecordings] = useState([]);
  const [recording, setRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [category, setCategory] = useState('diary');
  const [title, setTitle] = useState('');
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    if (token) loadRecordings();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, [token]);

  const loadRecordings = async () => {
    try {
      const res = await axios.get(`${API}/api/voice/recordings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecordings(res.data.recordings || []);
    } catch (err) {
      console.error('Load recordings error:', err);
    } finally {
      setLoading(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/mp4')
          ? 'audio/mp4'
          : 'audio/webm';

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.start(1000);
      setRecording(true);
      setDuration(0);

      timerRef.current = setInterval(() => {
        setDuration(d => d + 1);
      }, 1000);

    } catch (err) {
      console.error('Mic access error:', err);
      alert('Kunde inte komma åt mikrofonen. Kontrollera att du har gett tillåtelse.');
    }
  };

  const stopRecording = () => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
        resolve(null);
        return;
      }

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mediaRecorderRef.current.mimeType });
        resolve(blob);
      };

      mediaRecorderRef.current.stop();
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      setRecording(false);
    });
  };

  const handleStopAndSave = async () => {
    const blob = await stopRecording();
    if (!blob || blob.size === 0) return;

    setUploading(true);
    try {
      const formData = new FormData();
      const ext = blob.type.includes('mp4') ? 'm4a' : 'webm';
      formData.append('file', blob, `recording.${ext}`);
      formData.append('title', title.trim());
      formData.append('category', category);
      formData.append('duration', duration.toString());

      await axios.post(`${API}/api/voice/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setTitle('');
      setDuration(0);
      await loadRecordings();
    } catch (err) {
      console.error('Upload error:', err);
      alert('Kunde inte spara inspelningen');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/api/voice/recordings/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecordings(prev => prev.filter(r => r.id !== id));
    } catch {
      alert('Kunde inte ta bort inspelningen');
    }
  };

  const filtered = recordings.filter(r => {
    if (filter === 'diary') return r.category === 'diary';
    if (filter === 'note') return r.category === 'note';
    return true;
  });

  return (
    <div className="min-h-screen bg-background" data-testid="voice-recording-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="rounded-full" data-testid="voice-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-lg font-semibold">Röstinspelning</h1>
              <p className="text-xs text-muted-foreground">Dagbok & anteckningar med rösten</p>
            </div>
          </div>
          <Mic className="w-5 h-5 text-rose-500" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">

        {/* Record section */}
        <div className="bg-card rounded-2xl border border-border/30 p-6 text-center space-y-5" data-testid="record-section">
          {/* Category selector */}
          <div className="flex justify-center gap-2">
            <button
              onClick={() => setCategory('diary')}
              data-testid="category-diary"
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                category === 'diary'
                  ? 'bg-rose-100 text-rose-700 border border-rose-200'
                  : 'bg-secondary text-muted-foreground'
              }`}
            >
              <BookOpen className="w-4 h-4" />
              Dagbok
            </button>
            <button
              onClick={() => setCategory('note')}
              data-testid="category-note"
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                category === 'note'
                  ? 'bg-sky-100 text-sky-700 border border-sky-200'
                  : 'bg-secondary text-muted-foreground'
              }`}
            >
              <StickyNote className="w-4 h-4" />
              Anteckning
            </button>
          </div>

          {/* Title input */}
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Ge din inspelning en titel (valfritt)"
            className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 text-center"
            data-testid="recording-title-input"
          />

          {/* Timer */}
          {(recording || uploading) && (
            <div className="text-4xl font-heading font-light tabular-nums" data-testid="recording-timer">
              {formatDuration(duration)}
            </div>
          )}

          {/* Record button */}
          {!recording && !uploading && (
            <button
              onClick={startRecording}
              className="w-20 h-20 rounded-full bg-gradient-to-br from-rose-500 to-red-600 text-white flex items-center justify-center mx-auto shadow-lg hover:shadow-xl hover:scale-105 transition-all"
              data-testid="start-recording-btn"
            >
              <Mic className="w-8 h-8" />
            </button>
          )}

          {recording && (
            <div className="space-y-3">
              <div className="flex items-center justify-center gap-2">
                <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                <span className="text-sm text-red-600 font-medium">Spelar in...</span>
              </div>
              <button
                onClick={handleStopAndSave}
                className="w-20 h-20 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 text-white flex items-center justify-center mx-auto shadow-lg hover:shadow-xl hover:scale-105 transition-all"
                data-testid="stop-recording-btn"
              >
                <Square className="w-8 h-8" />
              </button>
            </div>
          )}

          {uploading && (
            <div className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-muted-foreground">Sparar inspelning...</span>
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            {recording ? 'Tryck på stoppknappen för att spara' : 'Tryck på mikrofonen för att börja spela in'}
          </p>
        </div>

        {/* Filter */}
        <div className="flex gap-2">
          {[
            { id: 'all', label: 'Alla' },
            { id: 'diary', label: 'Dagbok', icon: BookOpen },
            { id: 'note', label: 'Anteckningar', icon: StickyNote }
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`filter-${f.id}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full transition-colors ${
                filter === f.id
                  ? 'bg-primary text-white'
                  : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
              }`}
            >
              {f.icon && <f.icon className="w-3.5 h-3.5" />}
              {f.label}
            </button>
          ))}
        </div>

        {/* Recordings list */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-card rounded-2xl border border-border/30 p-8 text-center">
            <Mic className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">
              {recordings.length === 0
                ? 'Du har inga inspelningar ännu. Tryck på mikrofonen för att börja!'
                : 'Inga inspelningar i denna kategori'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(rec => (
              <RecordingItem
                key={rec.id}
                recording={rec}
                token={token}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
